import moment from "moment";
import {
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

import conf from "config-dlm";
import Refresh from "components/utils/Refresh";
import ErrorChip from "components/utils/ErrorChip";
import JsonModal from "components/utils/Json";
import EditDialog from "components/history/EditDialog";
import LastUpdated from "components/utils/LastUpdated";
import HistoryDialog from "components/history/HistoryDialog";
import ReleasesDialog from "components/releases/ReleasesDialog";
import { isFound, useSmartContent } from "components/utils/SmartCell";
import {
  ProjectConfigSchema,
  Snapshot,
  usePavmLive,
  HostBase,
} from "hooks/pavm/base";

const BaseRow = (props: {
  open: boolean;
  data: { meta: Snapshot["meta"]; clock: HostBase; kernel: HostBase };
  config: ProjectConfigSchema;
  version: string;
  updatedAt: Date | string | number | undefined;
}) => {
  const { open, config, data, version, updatedAt } = props;

  const host = config.host;

  const live = usePavmLive(host);
  const error = live?.error?.response?.data?.message;

  const OS = useSmartContent(data.meta.os);
  const Clock = useSmartContent(data.clock?.data);
  const Kernel = useSmartContent(data.kernel?.data);
  const Timezone = useSmartContent(data.meta.timezone);
  const Hardware = useSmartContent(data.meta.hardware);

  return (
    <TableRow
      sx={{
        bgcolor: (theme) =>
          theme.palette.mode === "light" ? "primary.light" : "primary.dark",
      }}
    >
      <TableCell colSpan={10}>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <EditDialog
                type="Edit config"
                config={config}
                version={version}
              />

              <HistoryDialog type="Host" label="History" config={config} />

              <ReleasesDialog type="Releases" config={config} />
            </Stack>

            <Stack direction="row" alignItems="center">
              {live.isError && (
                <ErrorChip
                  info={error}
                  label={<LastUpdated lastUpdatedTime={moment(updatedAt)} />}
                />
              )}

              {live.isSuccess && (
                <LastUpdated lastUpdatedTime={moment(updatedAt)} />
              )}

              <Refresh onClick={live.refetch} isFetching={live.isFetching} />
            </Stack>
          </Stack>

          <Table size="small" aria-label={`${host}-base`}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "text.secondary" }}>
                  <b>Base</b>
                </TableCell>

                {conf.COLUMNS.BASE.map((column: string) => (
                  <TableCell key={column} align="right">
                    <b>{column}</b>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              <TableRow key={`${host}-base`} sx={conf.LAST_CHILD_NO_BORDER}>
                <TableCell />

                <TableCell align="right">{Hardware}</TableCell>

                <TableCell align="right">{OS}</TableCell>

                <TableCell align="right">{Timezone}</TableCell>

                <TableCell align="right">
                  {isFound(data?.kernel?.data) ? (
                    Kernel
                  ) : (
                    <ErrorChip
                      size="small"
                      info={data?.kernel?.error ?? "No data found"}
                    />
                  )}
                </TableCell>

                <TableCell align="right">
                  {isFound(data?.clock.data) ? (
                    Clock
                  ) : (
                    <ErrorChip
                      size="small"
                      info={data.clock?.error ?? "No data found"}
                    />
                  )}
                </TableCell>

                <TableCell align="right">
                  <JsonModal type="Host" title={host} data={data} />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

export default BaseRow;
